import { useRef, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import styles from './timePicker.module.css';
import leftArrow from '../../assets/left-arrow.png';
import { FunctionComponent } from 'react';
import { getIdAttributes } from '../../utils/helper';

export const idPrefix: string = 'time-picker';
type TimePickerProps = {
  format?: '12-hour' | '24-hour'; // Toggle between 12-hour and 24-hour formats
  timeBreak?: number; // Time interval between options in minutes
  selectedTime: any; // Selected time passed from parent
  onTimeChange: (time: string) => void; // Callback for selected time
  placeholder?: JSX.Element;
  isDisabled?: boolean;
  minTime?: string; // Minimum time to start the options list
  maxTime?: string; // Maximum time to end the options list
  'data-testid'?: string;
};

const TimePicker: FunctionComponent<TimePickerProps> = ({
  format = '24-hour',
  timeBreak = 15,
  selectedTime,
  onTimeChange,
  placeholder = 'Select Time',
  isDisabled,
  minTime = format === '12-hour' ? '12:00 AM' : '00:00', // Default minTime
  maxTime, // MaxTime can be undefined
  'data-testid': dataTestId,
}) => {
  const prefix = dataTestId || idPrefix;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to parse time based on the current format
  const parseTime = (time: string): Moment => {
    return moment(
      time,
      format === '12-hour' ? ['hh:mm A', 'HH:mm'] : ['HH:mm', 'hh:mm A'],
    );
  };

  const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    const totalHours: number = 24; // 24-hour range

    const minMoment: Moment = parseTime(minTime);
    const maxMoment: Moment | null = maxTime ? parseTime(maxTime) : null;

    for (let hour = 0; hour < totalHours; hour++) {
      for (let minute = 0; minute < 60; minute += timeBreak) {
        const time: Moment = moment({ hour, minute });
        const formattedTime: string =
          format === '12-hour' ? time.format('hh:mm A') : time.format('HH:mm');

        // Add time if it's within the minTime and maxTime bounds
        if (
          time.isSameOrAfter(minMoment) && // Time should be after or equal to minTime
          (!maxMoment || time.isSameOrBefore(maxMoment)) // Time should be before or equal to maxTime if maxTime is provided
        ) {
          times.push(formattedTime);
        }
      }
    }
    return times;
  };

  const handleTimeClick = (time: string): void => {
    onTimeChange(time);
    setIsOpen(false); // Close dropdown after selection
  };

  const toggleDropdown = (): void => {
    setIsOpen(!isOpen);
  };

  // Handle click outside to close the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // Close dropdown if clicked outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      className={styles.timePicker}
      ref={dropdownRef}
      {...getIdAttributes(prefix, 'overlay')}
    >
      <div
        className={`${styles.selectedTime} ${isOpen && styles.active} ${
          selectedTime && styles.selectedView
        } ${isDisabled && styles.disabled}`}
        onClick={() => {
          if (!isDisabled) {
            toggleDropdown();
          }
        }}
        {...getIdAttributes(prefix, 'selected')}
      >
        <div {...getIdAttributes(prefix, 'selected-value')}>
          {selectedTime || placeholder}
        </div>
        <img src={leftArrow} alt="" {...getIdAttributes(prefix, 'arrow')} />
      </div>
      {isOpen && (
        <div
          {...getIdAttributes(prefix, 'dropdown-wrapper')}
          className={styles.timeListWrapper}
        >
          <div
            {...getIdAttributes(prefix, 'dropdown-list')}
            className={styles.timeList}
          >
            <div
              className={`${styles.timeOption}`}
              onClick={() => handleTimeClick('')}
              {...getIdAttributes(prefix, 'option-none')}
            >
              Select Time
            </div>
            {generateTimeOptions().map((time, index) => (
              <div
                {...getIdAttributes(
                  prefix,
                  `option-${time.replace(/[^a-zA-Z0-9]/g, '')}`,
                )}
                key={index}
                className={`${styles.timeOption} ${
                  selectedTime === time ? styles.selected : ''
                }`}
                onClick={() => handleTimeClick(time)}
              >
                {time}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
